import React, { useMemo } from "react";
import CodeReferencePreview from "organisms/code-reference-block-editor/code-reference-preview";
import CodeReferencePlaceholder from "organisms/code-reference-block-editor/code-reference-placeholder";
import { PageBlockContentProps } from "molecules/page-block-content/page-block-content";
import { isCodeReferenceAdditionalData } from "models/interfaces/additional-block-data/additional-code-reference-block-data";
import { CodeReferenceBlockConfigurationRecord } from "models/view-models/block-configurations/code-reference-block-configuration-record";
import { useCodeReferenceHighlightsApi } from "utilities/hooks/domain/notebooks/code-reference-blocks/use-code-reference-highlights-api";
import { RecordUtils } from "@rsm-hcd/javascript-core";
import useCodeReferenceBlockSectionApi from "utilities/hooks/domain/block-configurations/use-code-reference-block-section-api";
import Loader from "atoms/loaders/loader";

interface CodeReferenceBlockContentProps extends PageBlockContentProps {}

const CSS_CLASS_NAME = "c-code-reference-block-content";

const CodeReferenceBlockContent = ({
    block,
    additionalBlockData,
    printable = false,
    onBlockSaved,
}: CodeReferenceBlockContentProps) => {
    const codeRefConfiguration = useMemo(
        () =>
            RecordUtils.ensureRecord(
                block.configuration,
                CodeReferenceBlockConfigurationRecord
            ),
        [block.configuration]
    );
    const { loadingCodeReference } = useCodeReferenceBlockSectionApi(
        codeRefConfiguration.sectionId
    );
    const section = useMemo(() => {
        if (!isCodeReferenceAdditionalData(additionalBlockData)) {
            return null;
        }

        return additionalBlockData.section;
    }, [additionalBlockData]);

    const { deleteHighlight, updateHighlight } = useCodeReferenceHighlightsApi({
        block,
        handleFinished: (block?) => {
            if (block) onBlockSaved(block);
        },
    });

    if (loadingCodeReference) {
        return (
            <div className={`${CSS_CLASS_NAME}__loading`}>
                <Loader />
            </div>
        );
    }

    return (
        <div
            className={`${CSS_CLASS_NAME}`}
            data-test-id="page--code-reference-block--content">
            {section ? (
                <CodeReferencePreview
                    isEnhancedContentDisplayed={
                        codeRefConfiguration.isEnhancedContentDisplayed
                    }
                    highlights={codeRefConfiguration.highlights}
                    printable={printable}
                    referenceSection={section}
                    onUpdateHighlight={updateHighlight}
                    onDeleteHighlight={deleteHighlight}
                />
            ) : (
                <CodeReferencePlaceholder />
            )}
        </div>
    );
};

export default CodeReferenceBlockContent;
